import { DataCompartment, IMutation, MutationContext } from '@aesop-fables/scrinium';
import { TeamCompartments, teamStorageKey } from '..';
import { ApiKeys } from '../../../api/apis/ApiKeys';
import { TeamSelectionApi } from '../../../api/apis/TeamSelectionApi';
import { TeamApi } from '../../../api/apis/TeamApi';
import { PricebookApi } from '../../../api/apis/PricebookApi';
import { TeamData } from '../../../models/TeamData';
import { UserRole } from '../../../models/UserData';
import { SignUpCompartments, signUpStorageKey } from '../../signUp';
import { MetricsCompartments, metricsStorageKey } from '../../metrics';
import { RevShareCompartments, revShareStorageKey } from '../../rev-share';
import { PricebookCompartments, pricebookStorageKey } from '../../pricebook';
import { OrganizationCompartments, organizationStorageKey } from '../../organization';
import { UserCompartments, userStorageKey } from '../../user';
import { GroupCompartments, groupStorageKey } from '../../group';
import { GroupData } from '../../../models/GroupData';
import { hasRole } from '../../../helpers/roleUtils';

export interface CreateTeamInput {
  name: string;
  pricebookIds?: number[];
}

export class SaveTeamData implements IMutation<CreateTeamInput> {
  async execute(context: MutationContext<CreateTeamInput>): Promise<void> {
    const { data, storage, container } = context;
    const teamApi = container.get<TeamApi>(ApiKeys.Team);
    const response = await teamApi.post({ name: data.name });

    if (data.pricebookIds && response.data.id) {
      const teamSelectionApi = container.get<TeamSelectionApi>(ApiKeys.TeamSelection);
      await teamSelectionApi.select(response.data.id);

      const pricebookApi = container.get<PricebookApi>(ApiKeys.Pricebook);
      await pricebookApi.saveTeamOptions(data.pricebookIds);

      const pricebookCache = storage.retrieve<PricebookCompartments>(pricebookStorageKey);
      await pricebookCache.reload('teamAvailablePricebooks');
    }

    const teamDataCache = storage.retrieve<TeamCompartments>(teamStorageKey);
    const metricsDataCache = storage.retrieve<MetricsCompartments>(metricsStorageKey);

    await teamDataCache.reloadAll();
    await metricsDataCache.reload('groupMetrics');
  }
}

export class UpdateTeamData implements IMutation<TeamData> {
  async execute(context: MutationContext<TeamData>): Promise<void> {
    const { data, storage, container } = context;
    const teamApi = container.get<TeamApi>(ApiKeys.Team);

    const cache = storage.retrieve<TeamCompartments>(teamStorageKey);
    const currentData = (
      cache.findCompartment('team') as DataCompartment<TeamData>
    ).getData() as TeamData;

    if (currentData?.id) {
      await teamApi.update({ ...currentData, ...data });
    }

    const teamDataCache = storage.retrieve<TeamCompartments>(teamStorageKey);
    teamDataCache.reloadAll();
  }
}

export class SelectTeam implements IMutation<number> {
  async execute(context: MutationContext<number>): Promise<void> {
    const { data, storage, container } = context;
    const teamSelectionApi = container.get<TeamSelectionApi>(ApiKeys.TeamSelection);

    await teamSelectionApi.select(data);

    const teamDataCache = storage.retrieve<TeamCompartments>(teamStorageKey);
    const orgDataCache = storage.retrieve<OrganizationCompartments>(organizationStorageKey);
    const signUpDataCache = storage.retrieve<SignUpCompartments>(signUpStorageKey);

    await Promise.all([
      teamDataCache.reload('team'),
      orgDataCache.reload('allOrganizations'),
      orgDataCache.reload('allMetrics'),
      signUpDataCache.reload('allOrgSignUpStates'),
    ]);

    const userDataCache = storage.retrieve<UserCompartments>(userStorageKey);
    const roles = (
      userDataCache.findCompartment('userRoles') as DataCompartment<UserRole[]>
    ).getData() as UserRole[];

    const groupDataCache = storage.retrieve<GroupCompartments>(groupStorageKey);
    const group = (
      groupDataCache.findCompartment('group') as DataCompartment<GroupData>
    ).getData() as GroupData;

    const hasTeamRevenueRole =
      hasRole(roles, 'TEAM', 'REVENUE_ADMIN', data) ||
      hasRole(roles, 'GROUP', 'REVENUE_ADMIN', group?.id);

    if (hasTeamRevenueRole) {
      const revShareDataCache = storage.retrieve<RevShareCompartments>(revShareStorageKey);
      await revShareDataCache.reload('teamRevShareSettings');

      const pricebookCache = storage.retrieve<PricebookCompartments>(pricebookStorageKey);
      await pricebookCache.reload('teamAvailablePricebooks');
      await pricebookCache.reload('teamPricebooksInUse');
    } else {
      const revShareDataCache = storage.retrieve<RevShareCompartments>(revShareStorageKey);
      revShareDataCache.reset('teamRevShareSettings');

      const pricebookCache = storage.retrieve<PricebookCompartments>(pricebookStorageKey);
      pricebookCache.reset('teamAvailablePricebooks');
      pricebookCache.reset('teamPricebooksInUse');
    }
  }
}

export class UnselectTeam implements IMutation<number> {
  async execute(context: MutationContext<number>): Promise<void> {
    const { storage, container } = context;
    const teamSelectionApi = container.get<TeamSelectionApi>(ApiKeys.TeamSelection);

    await teamSelectionApi.unselect();

    const teamDataCache = storage.retrieve<TeamCompartments>(teamStorageKey);
    const signUpDataCache = storage.retrieve<SignUpCompartments>(signUpStorageKey);

    await Promise.all([teamDataCache.reload('team'), signUpDataCache.reset('allOrgSignUpStates')]);
  }
}

export class DeleteTeam implements IMutation<number> {
  async execute(context: MutationContext<number>): Promise<void> {
    const { data, storage, container } = context;
    const teamApi = container.get<TeamApi>(ApiKeys.Team);

    const teamDataCache = storage.retrieve<TeamCompartments>(teamStorageKey);

    await teamApi.delete(data);

    await teamDataCache.reloadAll();
  }
}
